import { useDisableUserMutation, useEnableUserMutation } from '@axilon-apps/domains-identity/state'
import { useFeedbackMessage } from '@axilon-apps/libs-ui-components'

interface ErrorResponse {
  message?: string
}

/**
 * Wraps the disable / enable user mutations with toast feedback. Used by the
 * Users table row actions (Global-Admin only). Disabling an account revokes its
 * sessions server-side; re-enabling restores login.
 */
const useSetUserEnabled = () => {
  const { toast } = useFeedbackMessage()
  const [disableUserMutation, disableResult] = useDisableUserMutation()
  const [enableUserMutation, enableResult] = useEnableUserMutation()

  const disable = async (userId: string, username?: string) => {
    const { error } = await disableUserMutation(userId)

    if (error) {
      toast.error({ message: (error as ErrorResponse).message || 'Failed to disable user' })
      return
    }

    toast.success({ message: `User ${username ?? ''} was disabled`.trim() })
  }

  const enable = async (userId: string, username?: string) => {
    const { error } = await enableUserMutation(userId)

    if (error) {
      toast.error({ message: (error as ErrorResponse).message || 'Failed to enable user' })
      return
    }

    toast.success({ message: `User ${username ?? ''} was enabled`.trim() })
  }

  return {
    disable,
    enable,
    isLoading: disableResult.isLoading || enableResult.isLoading
  }
}

export default useSetUserEnabled
